import React, { useState, useEffect } from 'react';
import { Bar, Doughnut, Line, Radar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, RadialLinearScale, Title, Tooltip, Legend, Filler } from 'chart.js';
import { Link } from 'react-router-dom';
import { FiDatabase, FiEye, FiDownload, FiTrendingUp, FiPieChart, FiArrowRight, FiFileText } from 'react-icons/fi';
import api from '../../utils/api';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, RadialLinearScale, Title, Tooltip, Legend, Filler);

const colors = ['#32966c','#e86f4c','#d4a012','#3b82f6','#8b5cf6','#ec4899','#14b8a6','#f97316','#6366f1','#ef4444'];

const AnalitikPage = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get('/datasets/statistik');
        setStats(res.data.data);
      } catch (e) { console.error(e); } finally { setLoading(false); }
    };
    load();
  }, []);

  if (loading) return <div className="loading-spinner" style={{ minHeight: '100vh', paddingTop: 200 }}><div className="spinner" /></div>;

  const kategoriData = {
    labels: stats?.byKategori?.map(k => k._id?.charAt(0).toUpperCase() + k._id?.slice(1)) || [],
    datasets: [{ data: stats?.byKategori?.map(k => k.count) || [], backgroundColor: colors, borderWidth: 0, borderRadius: 6 }]
  };

  const orgData = {
    labels: stats?.byOrganisasi?.map(o => o._id?.substring(0, 25) + (o._id?.length > 25 ? '...' : '')) || [],
    datasets: [{ label: 'Jumlah Dataset', data: stats?.byOrganisasi?.map(o => o.count) || [], backgroundColor: 'rgba(50,150,108,0.7)', borderRadius: 6, borderSkipped: false }]
  };

  const doughnutData = {
    labels: stats?.byKategori?.map(k => k._id?.charAt(0).toUpperCase() + k._id?.slice(1)) || [],
    datasets: [{ data: stats?.byKategori?.map(k => k.count) || [], backgroundColor: colors, borderWidth: 3, borderColor: '#fff' }]
  };

  return (
    <div style={{ paddingTop: 100, minHeight: '100vh' }}>
      <div className="container-wide">
        <div style={{ marginBottom: '2rem' }}>
          <h1 style={{ fontSize: 'clamp(1.5rem, 3vw, 2.2rem)', marginBottom: '0.5rem' }}>Analitik Data</h1>
          <p style={{ color: 'var(--gray-500)' }}>Visualisasi dan analisis data pembangunan Kabupaten Ngada</p>
        </div>

        {/* Summary Stats */}
        <div className="stats-grid" style={{ marginBottom: '2.5rem' }}>
          <div className="stat-card">
            <div className="stat-icon" style={{ background: 'var(--primary-100)', color: 'var(--primary-600)' }}><FiDatabase /></div>
            <div>
              <div className="stat-value">{stats?.totalDatasheet || 0}</div>
              <div className="stat-label">Total Datasheet</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon" style={{ background: 'var(--accent-100)', color: 'var(--accent-600)' }}><FiPieChart /></div>
            <div>
              <div className="stat-value">{stats?.totalVisual || 0}</div>
              <div className="stat-label">Visualisasi Hub</div>
            </div>
          </div>
          <div className="stat-card" style={{ borderLeft: '4px solid #10b981' }}>
            <div className="stat-icon" style={{ background: '#d1fae5', color: '#059669' }}><FiFileText /></div>
            <div>
              <div className="stat-value">{stats?.totalStatistik || 0}</div>
              <div className="stat-label">Produk Statistik</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon" style={{ background: 'var(--gold-100)', color: 'var(--gold-600)' }}><FiEye /></div>
            <div>
              <div className="stat-value">{stats?.totalViews?.toLocaleString('id-ID') || 0}</div>
              <div className="stat-label">Total Akses</div>
            </div>
          </div>
        </div>

        {/* Indikator Strategis (Enriched) */}
        <div style={{ marginBottom: '2.5rem' }}>
          <h3 style={{ marginBottom: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ color: 'var(--primary-600)' }}>★</span> Indikator Strategis Kabupaten
          </h3>
          <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
            <div className="card" style={{ padding: '1.25rem', borderLeft: '4px solid #e86f4c' }}>
              <div style={{ color: 'var(--gray-500)', fontSize: '0.85rem', marginBottom: '0.5rem' }}>Ketahanan Pangan</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>0.77 <span style={{ fontSize: '0.9rem', color: '#32966c', fontWeight: 500 }}>(Tahan)</span></div>
              <div style={{ fontSize: '0.75rem', color: 'var(--gray-400)', marginTop: '0.5rem' }}>Indeks Rata-rata 12 Kecamatan</div>
            </div>
            <div className="card" style={{ padding: '1.25rem', borderLeft: '4px solid #3b82f6' }}>
              <div style={{ color: 'var(--gray-500)', fontSize: '0.85rem', marginBottom: '0.5rem' }}>Eliminasi Malaria</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>67% <span style={{ fontSize: '0.9rem', color: '#3b82f6', fontWeight: 500 }}>(8/12 Kec)</span></div>
              <div style={{ fontSize: '0.75rem', color: 'var(--gray-400)', marginTop: '0.5rem' }}>Status Pemeliharaan/Tersertifikasi</div>
            </div>
            <div className="card" style={{ padding: '1.25rem', borderLeft: '4px solid #8b5cf6' }}>
              <div style={{ color: 'var(--gray-500)', fontSize: '0.85rem', marginBottom: '0.5rem' }}>Transformasi Digital</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>49 <span style={{ fontSize: '0.9rem', color: 'var(--gray-400)', fontWeight: 500 }}>Aplikasi</span></div>
              <div style={{ fontSize: '0.75rem', color: 'var(--gray-400)', marginTop: '0.5rem' }}>Total Inventaris Aplikasi OPD</div>
            </div>
          </div>

        {/* Dashboard Sektoral (Enriched) */}
        <div style={{ marginBottom: '2.5rem' }}>
          <h3 style={{ marginBottom: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ color: 'var(--accent-600)' }}>📁</span> Dashboard Sektoral Unggulan
          </h3>
          <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem' }}>
            <div className="card" style={{ padding: '1rem', textAlign: 'center', background: 'rgba(50, 150, 108, 0.05)', border: '1px solid rgba(50, 150, 108, 0.1)' }}>
              <div style={{ fontSize: '1.5rem', marginBottom: '0.25rem' }}>🌾</div>
              <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Pertanian</div>
              <div style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--primary-700)' }}>12+ <span style={{ fontSize: '0.8rem', fontWeight: 400 }}>Dataset</span></div>
            </div>
            <div className="card" style={{ padding: '1rem', textAlign: 'center', background: 'rgba(232, 111, 76, 0.05)', border: '1px solid rgba(232, 111, 76, 0.1)' }}>
              <div style={{ fontSize: '1.5rem', marginBottom: '0.25rem' }}>🐂</div>
              <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Peternakan</div>
              <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#e86f4c' }}>32K+ <span style={{ fontSize: '0.8rem', fontWeight: 400 }}>Populasi</span></div>
            </div>
            <div className="card" style={{ padding: '1rem', textAlign: 'center', background: 'rgba(212, 160, 18, 0.05)', border: '1px solid rgba(212, 160, 18, 0.1)' }}>
              <div style={{ fontSize: '1.5rem', marginBottom: '0.25rem' }}>🏔️</div>
              <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Pariwisata</div>
              <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#d4a012' }}>27 <span style={{ fontSize: '0.8rem', fontWeight: 400 }}>Desa Wisata</span></div>
            </div>
            <div className="card" style={{ padding: '1rem', textAlign: 'center', background: 'rgba(59, 130, 246, 0.05)', border: '1px solid rgba(59, 130, 246, 0.1)' }}>
              <div style={{ fontSize: '1.5rem', marginBottom: '0.25rem' }}>🏠</div>
              <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Perumahan</div>
              <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#3b82f6' }}>82% <span style={{ fontSize: '0.8rem', fontWeight: 400 }}>Milik Sendiri</span></div>
            </div>
            <div className="card" style={{ padding: '1rem', textAlign: 'center', background: 'rgba(20, 184, 166, 0.05)', border: '1px solid rgba(20, 184, 166, 0.1)' }}>
              <div style={{ fontSize: '1.5rem', marginBottom: '0.25rem' }}>💧</div>
              <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Air Minum</div>
              <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#14b8a6' }}>72.5% <span style={{ fontSize: '0.8rem', fontWeight: 400 }}>Akses Layak</span></div>
            </div>
          </div>
        </div>
        </div>

        {/* Charts Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(450px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
          <div className="chart-container">
            <h3>📊 Dataset per Kategori</h3>
            <div style={{ height: 350 }}>
              <Bar data={kategoriData} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { y: { beginAtZero: true, grid: { color: '#eaf0f5' } }, x: { grid: { display: false } } } }} />
            </div>
          </div>
          <div className="chart-container">
            <h3>🍩 Distribusi Kategori</h3>
            <div style={{ height: 350, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Doughnut data={doughnutData} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'right', labels: { boxWidth: 12, padding: 12, font: { family: 'Plus Jakarta Sans', size: 11 } } } } }} />
            </div>
          </div>
        </div>

        {/* Visualisations Hub Gallery */}
        <div style={{ marginBottom: '3rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h3 style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                  <FiPieChart style={{ color: 'var(--accent-600)' }} /> Visualisations Hub ({stats?.totalVisual || 0})
                </h3>
                <Link to="/dataset?type=visual" style={{ fontSize: '0.85rem', color: 'var(--primary-600)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                  Lihat Semua Galeri <FiArrowRight />
                </Link>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem' }}>
                {[
                  { title: 'Peta Sebaran Stunting Ngada', cat: 'kesehatan', img: 'https://images.unsplash.com/photo-1576091160550-2173bdb999ef?q=80&w=400' },
                  { title: 'Dashboard Ketahanan Pangan', cat: 'pertanian', img: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?q=80&w=400' },
                  { title: 'Analisis Wisatawan Mancanegara', cat: 'pariwisata', img: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?q=80&w=400' }
                ].map((v, i) => (
                    <div key={i} className="card" style={{ padding: 0, overflow: 'hidden', transition: 'all 0.3s', cursor: 'pointer', border: '1px solid var(--gray-100)' }} onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-5px)'} onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}>
                        <div style={{ height: 160, position: 'relative' }}>
                            <img src={v.img} alt={v.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            <div style={{ position: 'absolute', top: 12, left: 12 }}>
                              <span className={`card-kategori kat-${v.cat}`} style={{ boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>{v.cat}</span>
                            </div>
                        </div>
                        <div style={{ padding: '1.25rem' }}>
                            <h4 style={{ fontSize: '1.05rem', marginBottom: '0.5rem' }}>{v.title}</h4>
                            <p style={{ fontSize: '0.8rem', color: 'var(--gray-500)', lineHeight: '1.5' }}>
                              Dashboard interaktif yang menyajikan data spasial dan temporal secara dinamis untuk pemantauan target pembangunan.
                            </p>
                        </div>
                    </div>
                ))}
            </div>
        </div>

        {/* Recent Datasets */}
        {stats?.recentDatasets?.length > 0 && (
          <div className="card" style={{ marginBottom: '2rem' }}>
            <div className="card-body">
              <h3 style={{ fontFamily: 'var(--font-body)', fontSize: '1.1rem', marginBottom: '1rem' }}>📋 Dataset Terbaru</h3>
              <div className="table-container" style={{ border: 'none' }}>
                <table className="data-table">
                  <thead>
                    <tr><th>Judul</th><th>Kategori</th><th>Tanggal</th></tr>
                  </thead>
                  <tbody>
                    {stats.recentDatasets.map(ds => (
                      <tr key={ds._id}>
                        <td><strong>{ds.judul}</strong></td>
                        <td><span className={`card-kategori kat-${ds.kategori}`}>{ds.kategori}</span></td>
                        <td style={{ color: 'var(--gray-400)' }}>{new Date(ds.createdAt).toLocaleDateString('id-ID')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalitikPage;
